import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import uploadIconLight from "../../../assets/images/LightTheme/upload_light.svg";
import uploadIconDark from "../../../assets/images/DarkTheme/upload_dark.svg";
import { useAppSelector } from "../../../redux/hooks";
import styles from "./HATVDocument.module.scss";
import {
  SuccessionPlanFileType,
  IGoalActionPlanColumnDetails,
  ITableColumnState,
  deleteDocType,
  toastMessageType,
  NestedObject,
} from "../../../types/types";
import DocumentTable from "../../common/DocumentTable/DocumentTable";
import Modal from "../../common/Modal/Modal";
import { isEmpty, isNull } from "lodash";
import UploadComponent from "../../common/UploadComponent/UploadComponent";
import UploadedDocView from "../../common/UploadedDocView/UploadedDocView";
import {
  useGetHatvDocumentListQuery,
  useUploadHatvDocumentListMutation,
  useDeleteHatvDocumentListMutation,
} from "../../../redux/services/hatvDocumentAPI";
import { getLoggedUserId, isOwner, useProfileParams } from "../../../utils/common";
import { useSelector } from "react-redux";
import { RootState } from "../../../redux/store";
import { disableEnableScroll } from "../../../utils/disableEnableScroll";
import SuccessionDocLoader from "../../common/Loaders/SuccessionDocLoader/SuccessionDocLoader";
import ErrorInPageScreen from "../../common/ErrorInPageScreen/ErrorInPageScreen";
import ToastMessage from "../../common/ToastMessage/ToastMessage";
import DeleteConfirmationPopUp from "../../common/DeleteConfirmationPopUp/DeleteConfirmationPopUp";
import { isValidResponse } from "../../../utils/validateAPIResponse";
import { findKeysInArray } from "../../../utils/common";
import { sortTableData } from "../../../utils/common";
import { getYearFromQMonth } from "../../../utils/common";
import { useMonthOptions } from "../../Context/MonthYearOptionsContext";

const DeleteDocConfirmationModuleContentStyle: React.CSSProperties = {
  width: "384px",
};

const customButtonStyles: React.CSSProperties = {
  textAlign: "center",
  fontFamily: "gt-walsheim-regular",
  fontSize: "14px",
  fontStyle: "normal",
  fontWeight: "700",
  lineHeight: "18px",
};

interface HATVDocsProps {
  type: string;
  selectedTabData?: NestedObject
}

const HATVDocument: React.FC<HATVDocsProps> = ({ type, selectedTabData }) => {
  const allowedFileSize = 30 * 1024 * 1024; // allowed filesize for HATV - 30mb
  const { t } = useTranslation();
  const { selectedYearMonth } = useMonthOptions();
  const currentTheme = useAppSelector((state: any) => state.theme.currentTheme);
  const uploadIcon =
    currentTheme === "light" ? uploadIconLight : uploadIconDark;

  const [fileToBeUploaded, setFileToBeUploaded] =
    useState<SuccessionPlanFileType>();
  const [hatvDocumentList, setHatvDocumentList] = useState<
    IGoalActionPlanColumnDetails[] | []
  >([]);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteDocData, setDeleteDocData] = useState<deleteDocType>();
  const [modalOpen, setModalOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [columnState, setColumnState] = useState<
    ITableColumnState<IGoalActionPlanColumnDetails>[]
  >([]);
  const [isSuccessDelete, setIsSccessDelete] =
    useState<toastMessageType>(undefined);
  const [documentTypeList, setDocumentTypeList] = useState<DropdownItem[]>();
  const [showToast, setShowToast] = useState(false);
  const [isAPIError, setIsAPIError] = useState<boolean>(false);

  const selectedYear = selectedYearMonth ? getYearFromQMonth(selectedYearMonth) : "";
  
  const { affiliateCode, aboNumber } = useProfileParams();

  const { isFetching, data, isError } = useGetHatvDocumentListQuery(
    {
      affiliateCode,
      aboNumber,
      year: selectedYear,
      type,
    },
    { skip: isAPIError || !selectedYear}
  );

  const defaultColumnName =
    columnState[0]?.sortOrder === "nosort" || isEmpty(columnState)
      ? "updatedDate"
      : columnState[0]?.column;
  const defaultSort =
    columnState[0]?.sortOrder === "nosort" || isEmpty(columnState)
      ? "desc"
      : columnState[0]?.sortOrder;

  const [
    updateHatvDocsList,
    { isLoading: isLoadingDeleteHatvDocument, isError: isErrorInDelete },
  ] = useDeleteHatvDocumentListMutation();

  // Pass exact key names from response to table data
  const tableColumnData = ["aboActionPlanId", "name", "size", "updatedDate", "metadata"];

  const handleAPIError = () => {
    setIsAPIError(true);
    setHatvDocumentList([]);
  };

  useEffect(() => {
    disableEnableScroll(modalOpen);
  }, [modalOpen]);

  useEffect(() => {
    if (isError || isErrorInDelete) {
      handleAPIError();
    }
  }, [isError, isErrorInDelete]);

  useEffect(() => {
    if (!isEmpty(data)) {
      if (isValidResponse(data?.statusCode)) {
        let prepareTableData = findKeysInArray(
          data?.body ?? [],
          tableColumnData
        );
        // create a common key to use ID's different name
        prepareTableData?.forEach((docRow) => {
          docRow["commonIdKey"] = docRow.aboActionPlanId;
          const original = (data?.body ?? []).find(
            (item: any) => item?.aboActionPlanId === docRow?.aboActionPlanId
          );
          docRow["isOwner"] = isOwner(original?.audit?.createdBy);
        });
        const intialSortData = sortTableData(
          prepareTableData,
          defaultColumnName,
          defaultSort
        );
        setHatvDocumentList(intialSortData);
      } else {
        handleAPIError();
      }
    }
  }, [data, isFetching]);

  const deleteDocument = async (docName: string, id: string | number) => {
    handleDeleteModalToggle();
    setDeleteDocData({
      docName: docName,
      id: id,
    });
  };

  const handleModalToggle = () => {
    setModalOpen(!modalOpen);
    setFileToBeUploaded(undefined);
  };

  const handlePersistSelectedFile = (file: SuccessionPlanFileType) => {
    setFileToBeUploaded(file);
  };

  const handleDeleteModalToggle = () => {
    setDeleteModalOpen(!deleteModalOpen);
  };

  const confirmDeleteDocs = async () => {
    if (deleteDocData && !isNull(deleteDocData)) {
      handleDeleteModalToggle();
      updateHatvDocsList({
        id: deleteDocData?.id,
        type,
      })
        .then((resp: any) => {
          if (resp?.data?.statusCode === 200) {
            setToastMessage(t("item_deleted_success"));
            setIsSccessDelete("success");
          } else {
            handleAPIError();
            setToastMessage(t("item_deleted_failure"));
            setIsSccessDelete("error");
          }
        })
        .finally(() => {
          setShowToast(true);
          setTimeout(() => {
            setShowToast(false);
            setIsSccessDelete(undefined);
            setToastMessage(null);
          }, 3000);
        });

      setDeleteDocData(undefined);
    }
  };

  return (
    <section
      className={`hatv_document_container ${styles.hatv_document_container}`}
    >
      {isFetching || isLoadingDeleteHatvDocument ? (
        <SuccessionDocLoader num={1} heading={t("documents")} />
      ) : isAPIError ? (
        <>
          <div className={styles.title_wrapper}>
            <div className={styles.title}>{t("documents")}</div>
          </div>

          <ErrorInPageScreen
            handleClickTryAgain={() => {
              setIsAPIError(false);
            }}
          />
        </>
      ) : (
        <>
          <div className={styles.title_wrapper}>
            <div className={styles.title}>{t("documents")}</div>
            <div
              role="button"
              className={styles.icon_wrapper}
              onClick={handleModalToggle}
            >
              <img
                src={uploadIcon}
                alt="upload"
                className={styles.upload_icon}
                data-testid="upload_icon_testID"
              />
              <span className={`label ${styles.label}`}>{t("upload")}</span>
            </div>
          </div>

          <DocumentTable
            tableData={hatvDocumentList}
            isLoading={isFetching}
            columnState={columnState}
            setColumnState={setColumnState}
            deleteDocument={deleteDocument}
            rowLimit={5}
            columnsToBeVisible={["docName", "violationPeriod", "date", "actions"]}
            downloadDocUrlParam={"actionplan"}
            screenName={"hatvdocument"}
            type={type}
          />
        </>
      )}

      <Modal showDrawer={modalOpen} onClose={handleModalToggle}>
        {isEmpty(fileToBeUploaded) ? (
          <UploadComponent
            handlePersistSelectedFile={handlePersistSelectedFile}
            isSourceHatv={true}
            allowedFileSize={allowedFileSize}
          />
        ) : (
          <UploadedDocView
            file={fileToBeUploaded}
            documentTypeList={documentTypeList}
            handleCancelCallback={handleModalToggle}
            handleTogglePopupCallback={() => {
              handleModalToggle();
            }}
            handleClickDeleteCallback={() => {
              setFileToBeUploaded(undefined);
            }}
            isEnabledDropdown={false}
            section="analysis_doc"
            uploadMutation={useUploadHatvDocumentListMutation}
            type={type}
            setToastMessage={setToastMessage}
            setShowToast={setShowToast}
            setIsSccessUpload={setIsSccessDelete}
            sourceScreenName={"hatvdocument"}
            selectedTabData={selectedTabData}
            selectedYearMonth={selectedYearMonth || ''}
          />
        )}
      </Modal>
      {showToast && (
        <ToastMessage
          message={toastMessage}
          subText=""
          toastType={isSuccessDelete}
          showToast={showToast}
          setShowToast={setShowToast}
        />
      )}

      <Modal
        showDrawer={deleteModalOpen}
        onClose={handleDeleteModalToggle}
        customStyle={DeleteDocConfirmationModuleContentStyle}
      >
        <DeleteConfirmationPopUp
          id={"hatvDocDeleteConfirmation"}
          titleLabel={t("delete_confirmation_headerText")}
          msgText={t("delete_confirmation_msg")}
          buttonLabelOne={t("no_cancel")}
          buttonLabelTwo={t("yes_delete")}
          onCancel={handleDeleteModalToggle}
          onDelete={confirmDeleteDocs}
          customStyle={customButtonStyles}
        />
      </Modal>
    </section>
  );
};

export default HATVDocument;
